import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface Template {
  id: number;
  name: string;
  category: string;
  project_id: number | null;
  description: string | null;
  is_active: boolean;
}

interface TemplateItem {
  id: number;
  label: string;
  is_required: boolean;
  position: number;
}

interface PagedResponse<T> {
  data: {
    items: T[];
    total: number;
  };
}

interface ProjectOption {
  id: number;
  name: string;
}

const emptyTemplateForm = {
  id: null as number | null,
  name: '',
  category: '',
  project_id: '' as number | '' ,
  description: '',
  is_active: true,
};

const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyTemplateForm>(emptyTemplateForm);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemRequired, setNewItemRequired] = useState(false);
  const [newItemPosition, setNewItemPosition] = useState<number | ''>('');

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templatesRes, projectsRes] = await Promise.all([
        apiClient.get<PagedResponse<Template>>('/audit-templates'),
        apiClient.get<PagedResponse<ProjectOption>>('/projects', {
          params: { per_page: 200 },
        }),
      ]);
      setTemplates(templatesRes.data.data.items || []);
      setProjects(projectsRes.data.data.items || []);
    } catch (e) {
      setError('No se pudieron cargar las plantillas');
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (templateId: number) => {
    setItemsLoading(true);
    setItemsError(null);
    try {
      const res = await apiClient.get<{ data: { items: TemplateItem[] } }>(
        `/audit-templates/${templateId}/items`,
      );
      const loaded = res.data.data.items || [];
      setItems(
        loaded.slice().sort((a, b) => {
          if (a.position === b.position) return a.id - b.id;
          return a.position - b.position;
        }),
      );
    } catch (e) {
      setItems([]);
      setItemsError('No se pudieron cargar los ítems de la plantilla');
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const startCreate = () => {
    setError(null);
    setForm(emptyTemplateForm);
    setSelectedTemplateId(null);
    setItems([]);
  };

  const startEdit = (tpl: Template) => {
    setError(null);
    setForm({
      id: tpl.id,
      name: tpl.name,
      category: tpl.category,
      project_id: tpl.project_id ?? '',
      description: tpl.description ?? '',
      is_active: tpl.is_active,
    });
    setSelectedTemplateId(tpl.id);
    loadItems(tpl.id);
  };

  const handleTemplateChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type, checked } = e.target as any;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTemplateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = {
      name: form.name,
      category: form.category,
      project_id:
        typeof form.project_id === 'string'
          ? form.project_id === ''
            ? null
            : parseInt(form.project_id, 10)
          : form.project_id,
      description: form.description || null,
      is_active: form.is_active,
    };

    try {
      if (form.id == null) {
        await apiClient.post('/audit-templates', payload);
      } else {
        await apiClient.patch(`/audit-templates/${form.id}`, payload);
      }
      setForm(emptyTemplateForm);
      await loadTemplates();
    } catch (e) {
      setError('No se pudo guardar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (tpl: Template) => {
    setSelectedTemplateId(tpl.id);
    loadItems(tpl.id);
  };

  const handleCreateItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplateId || !newItemLabel.trim()) return;

    setItemsLoading(true);
    setItemsError(null);
    try {
      const res = await apiClient.post<{ data: TemplateItem }>(
        `/audit-templates/${selectedTemplateId}/items`,
        {
          label: newItemLabel.trim(),
          is_required: newItemRequired,
          position:
            newItemPosition === '' ? undefined : Number.parseInt(String(newItemPosition), 10),
        },
      );
      const created = res.data.data;
      setItems(prev =>
        [...prev, created].sort((a, b) => {
          if (a.position === b.position) return a.id - b.id;
          return a.position - b.position;
        }),
      );
      setNewItemLabel('');
      setNewItemRequired(false);
      setNewItemPosition('');
    } catch (e) {
      setItemsError('No se pudo crear el ítem de plantilla');
    } finally {
      setItemsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '16px 24px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Plantillas de auditoría
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Define plantillas reutilizables de checklist por proyecto y categoría.
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 2fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        {/* Formulario plantilla */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              {form.id == null ? 'Nueva plantilla' : `Editar plantilla #${form.id}`}
            </h2>
            <button
              type="button"
              onClick={startCreate}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Nueva
            </button>
          </div>

          <form onSubmit={handleTemplateSubmit}>
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Nombre
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleTemplateChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Categoría
              </label>
              <input
                type="text"
                name="category"
                value={form.category}
                onChange={handleTemplateChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Proyecto (opcional)
              </label>
              <select
                name="project_id"
                value={form.project_id}
                onChange={handleTemplateChange}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Todas los proyectos</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} (#{p.id})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Descripción
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleTemplateChange}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                }}
              />
            </div>

            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
              }}
            >
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleTemplateChange}
              />
              Plantilla activa
            </label>

            {error && (
              <div
                style={{
                  color: '#fecaca',
                  background: 'rgba(220, 38, 38, 0.12)',
                  border: '1px solid rgba(248, 113, 113, 0.4)',
                  borderRadius: 10,
                  padding: '6px 8px',
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 10,
                padding: '8px 14px',
                borderRadius: 999,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  loading
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
              }}
            >
              {loading ? 'Guardando...' : 'Guardar plantilla'}
            </button>
          </form>
        </section>

        {/* Listado + checklist de la plantilla seleccionada */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Plantillas existentes
            </h2>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Total: {templates.length}</span>
          </div>

          {loading && <p style={{ fontSize: 12 }}>Cargando...</p>}
          {!loading && templates.length === 0 && (
            <p style={{ fontSize: 12 }}>No hay plantillas definidas.</p>
          )}
          {!loading && templates.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                    }}
                  >
                    <th style={{ padding: '6px 4px' }}>ID</th>
                    <th style={{ padding: '6px 4px' }}>Nombre</th>
                    <th style={{ padding: '6px 4px' }}>Categoría</th>
                    <th style={{ padding: '6px 4px' }}>Proyecto</th>
                    <th style={{ padding: '6px 4px' }}>Activa</th>
                    <th style={{ padding: '6px 4px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map(tpl => (
                    <tr
                      key={tpl.id}
                      style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                    >
                      <td style={{ padding: '6px 4px' }}>{tpl.id}</td>
                      <td style={{ padding: '6px 4px' }}>{tpl.name}</td>
                      <td style={{ padding: '6px 4px' }}>{tpl.category}</td>
                      <td style={{ padding: '6px 4px' }}>
                        {tpl.project_id != null ? `#${tpl.project_id}` : '-'}
                      </td>
                      <td style={{ padding: '6px 4px' }}>{tpl.is_active ? 'Sí' : 'No'}</td>
                      <td style={{ padding: '6px 4px' }}>
                        <button
                          type="button"
                          onClick={() => startEdit(tpl)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(148, 163, 184, 0.7)',
                            backgroundColor: 'transparent',
                            color: '#e5e7eb',
                            fontSize: 11,
                            cursor: 'pointer',
                            marginRight: 4,
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSelectTemplate(tpl)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: 999,
                            border: '1px solid rgba(59, 130, 246, 0.7)',
                            backgroundColor: 'rgba(30, 64, 175, 0.35)',
                            color: '#bfdbfe',
                            fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Ver checklist
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedTemplateId && (
            <div
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: '1px solid rgba(55, 65, 81, 0.8)',
              }}
            >
              <h3 style={{ margin: '0 0 8px', fontSize: 13 }}>
                Checklist de la plantilla #{selectedTemplateId}
              </h3>

              {itemsError && (
                <div style={{ color: '#fecaca', marginBottom: 8, fontSize: 12 }}>{itemsError}</div>
              )}
              {itemsLoading && <p style={{ fontSize: 12 }}>Cargando checklist...</p>}
              {!itemsLoading && items.length === 0 && (
                <p style={{ fontSize: 12 }}>No hay ítems definidos para esta plantilla.</p>
              )}
              {!itemsLoading && items.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
                  {items.map(it => (
                    <li
                      key={it.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0',
                        fontSize: 12,
                      }}
                    >
                      <span
                        style={{
                          minWidth: 32,
                          fontVariantNumeric: 'tabular-nums',
                          color: '#9ca3af',
                        }}
                      >
                        {it.position}
                      </span>
                      <span style={{ flex: 1 }}>{it.label}</span>
                      {it.is_required && (
                        <span style={{ color: '#f97316', fontSize: 11 }}>obligatorio</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={handleCreateItem} style={{ marginTop: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input
                    type="text"
                    placeholder="Nuevo ítem de checklist"
                    value={newItemLabel}
                    onChange={e => setNewItemLabel(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      borderRadius: 999,
                      border: '1px solid rgba(148, 163, 184, 0.6)',
                      backgroundColor: 'rgba(15, 23, 42, 0.95)',
                      color: '#e5e7eb',
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Posición"
                    value={newItemPosition}
                    onChange={e => {
                      const v = e.target.value;
                      setNewItemPosition(v === '' ? '' : Number.parseInt(v, 10) || '');
                    }}
                    style={{
                      width: 80,
                      padding: '6px 8px',
                      borderRadius: 999,
                      border: '1px solid rgba(148, 163, 184, 0.6)',
                      backgroundColor: 'rgba(15, 23, 42, 0.95)',
                      color: '#e5e7eb',
                    }}
                  />
                </div>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginBottom: 6,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={newItemRequired}
                    onChange={e => setNewItemRequired(e.target.checked)}
                  />
                  Obligatorio
                </label>
                <div>
                  <button
                    type="submit"
                    disabled={itemsLoading || !newItemLabel.trim()}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 999,
                      border: 'none',
                      fontSize: 12,
                      fontWeight: 500,
                      cursor:
                        itemsLoading || !newItemLabel.trim() ? 'default' : 'pointer',
                      color: '#0b1120',
                      background:
                        itemsLoading || !newItemLabel.trim()
                          ? 'linear-gradient(to right, #6b7280, #4b5563)'
                          : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
                    }}
                  >
                    {itemsLoading ? 'Guardando ítem...' : 'Añadir ítem'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default TemplatesPage;
